
/**
 * Module dependencies.
 */

import { HistoryItemProps } from 'src/types/histories';
import { color, media, units } from 'src/styles/utils';
import { normalizeNewLine } from 'src/core/utils/strings';
import { theme } from 'styled-tools';
import GalleryNavigation from 'src/components/core/slider/gallery-navigation';
import Image from 'src/components/core/image';
import Lightbox from 'src/components/lightbox';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement, useCallback, useRef, useState } from 'react';
import Slider, { Slide } from 'src/components/core/slider';
import Type from 'src/components/core/typography';
import map from 'lodash/map';
import size from 'lodash/size';
import styled from 'styled-components';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: grid;
  grid-template-areas:
    'title'
    'text'
    'gallery';
  grid-template-columns: 100%;
  grid-template-rows: repeat(3, max-content);
  padding-top: ${units(4)};

  ${media.min('ms')`
    grid-template-areas:
      '.       title   .'
      '.       text    .'
      'gallery gallery gallery';
    grid-template-columns: 1fr 10fr 1fr;
    grid-template-rows: repeat(3, max-content);
    padding-top: ${units(6)};
  `}

  ${media.min('md')`
    grid-template-columns: 2fr 8fr 2fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H6).attrs({ as: 'h3' })`
  font-family: ${theme('typography.fontFamily.sansSerif')};
  grid-area: title;
  margin-bottom: ${units(2.5)};

  ${media.min('ms')`
    margin-bottom: ${units(4)};
  `}
`;

/**
 * `Text` styled component.
 */

const Text = styled(Type.Paragraph)`
  color: ${color('grey400')};
  grid-area: text;
`;

/**
 * `GalleryWrapper` styled component.
 */

const GalleryWrapper = styled.div`
  display: grid;
  grid-area: gallery;
  grid-template-areas:
    'image'
    'dots';
  grid-template-columns: 100%;
  grid-template-rows: max-content ${units(7)};
  margin-top: ${units(4)};
  width: 100%;

  ${media.min('ms')`
    margin-top: ${units(6)};
  `}
`;

/**
 * `Gallery` styled component.
 */

const Gallery = styled(Slider)`
  grid-area: image;
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  cursor: pointer;
  margin-bottom: ${units(2)};
  overflow: hidden;
  padding-bottom: 64.4%;
  position: relative;
`;

/**
 * `GalleryDetailsDots` styled component.
 */

const GalleryDetailsDots = styled.div`
  align-items: center;
  display: none;
  grid-area: dots;
  justify-content: flex-end;

  ${media.min('ms')`
    display: flex;
  `}
`;

/**
 * `HistoryItem` component.
 */

const HistoryItem = (props: HistoryItemProps): ReactElement => {
  const { media, text, title } = props;
  const sliderRef = useRef<any>();
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [isOpenLightbox, setOpenLightbox] = useState<boolean>();
  const gallerySize = size(media);
  const handleSlideChange = useCallback(({ activeIndex }) => {
    setActiveSlide(activeIndex);
  }, []);

  const handleClickSlide = useCallback(() => {
    setOpenLightbox(true);
  }, []);

  const handleClickDot = useCallback((index: number) => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.slideTo(index);
    }
  }, []);

  return (
    <>
      <Wrapper>
        {title && (
          <Title>
            {title}
          </Title>
        )}

        <RawHtml element={Text}>
          {normalizeNewLine(text)}
        </RawHtml>

        {gallerySize > 0 && (
          <GalleryWrapper>
            <Gallery
              onClick={handleClickSlide}
              onSlideChange={handleSlideChange}
              ref={sliderRef}
            >
              {map(media, ({ fileUrl }, index: number) => (
                <Slide key={index}>
                  <ImageWrapper>
                    <Image
                      alt={title}
                      layout={'fill'}
                      objectFit={'cover'}
                      src={fileUrl}
                    />
                  </ImageWrapper>
                </Slide>
              ))}
            </Gallery>

            {gallerySize > 1 && (
              <GalleryDetailsDots>
                <GalleryNavigation
                  current={activeSlide}
                  displayPagination
                  onGoToNext={() => handleClickDot(activeSlide + 1)}
                  onGoToPrevious={() => handleClickDot(activeSlide - 1)}
                  total={gallerySize}
                />
              </GalleryDetailsDots>
            )}
          </GalleryWrapper>
        )}
      </Wrapper>

      {gallerySize > 0 && (
        <Lightbox
          initialSlide={activeSlide}
          isOpen={isOpenLightbox}
          items={map(media, ({ fileUrl }) => ({
            type: 'image',
            url: fileUrl
          }))}
          onClose={() => setOpenLightbox(false)}
        />
      )}
    </>
  );
};

/**
 * Export `HistoryItem` component.
 */

export default HistoryItem;
